/*
  This files is used to pre-build data during the build process.
  Avoid adding client-side code here, as it can break the build process.
*/
import { zeroAddress } from "viem";

import type { ERC20Address, NativeTokenSupportedAddress, Token } from "types/tokens";

import { ARBITRUM, ARBITRUM_SEPOLIA, AVALANCHE, AVALANCHE_FUJI, BOTANIX, ContractsChainId } from "./chains";
import { convertTokenAddress, getToken, getTokenBySymbol } from "./tokens";

export const SWAP_GRAPH_MAX_MARKETS_PER_TOKEN = 5;

export type MarketConfig = {
  marketTokenAddress: string;
  indexTokenAddress: string;
  longTokenAddress: string;
  shortTokenAddress: string;
};

export type MarketsConfigMap = Record<string, MarketConfig>;

/*
  ATTENTION
  When adding new markets, please add them also to the end of the list in ./src/configs/static/sortedMarkets.ts
*/
export const MARKETS: Record<ContractsChainId, MarketsConfigMap> = {
  [ARBITRUM]: {
    // BTC/USD [WBTC.e-USDC]
    "0x47c031236e19d024b42f8AE6780E44A573170703": {
      marketTokenAddress: "0x47c031236e19d024b42f8AE6780E44A573170703",
      indexTokenAddress: "0x47904963fc8b2340414262125aF798B9655E58Cd",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ETH/USD [WETH-USDC]
    "0x70d95587d40A2caf56bd97485aB3Eec10Bee6336": {
      marketTokenAddress: "0x70d95587d40A2caf56bd97485aB3Eec10Bee6336",
      indexTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // DOGE/USD [WETH-USDC]
    "0x6853EA96FF216fAb11D2d930CE3C508556A4bdc4": {
      marketTokenAddress: "0x6853EA96FF216fAb11D2d930CE3C508556A4bdc4",
      indexTokenAddress: "0xC4da4c24fd591125c3F47b340b6f4f76111883d8",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SOL/USD [SOL-USDC]
    "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9": {
      marketTokenAddress: "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9",
      indexTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
      longTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // LTC/USD [WETH-USDC]
    "0xD9535bB5f58A1a75032416F2dFe7880C30575a41": {
      marketTokenAddress: "0xD9535bB5f58A1a75032416F2dFe7880C30575a41",
      indexTokenAddress: "0xB46A094Bc4B0adBD801E14b9DB95e05E28962764",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // UNI/USD [UNI-USDC]
    "0xc7Abb2C5f3BF3CEB389dF0Eecd6120D451170B50": {
      marketTokenAddress: "0xc7Abb2C5f3BF3CEB389dF0Eecd6120D451170B50",
      indexTokenAddress: "0xFa7F8980b0f1E64A2062791cc3b0871572f1F7f0",
      longTokenAddress: "0xFa7F8980b0f1E64A2062791cc3b0871572f1F7f0",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // LINK/USD [LINK-USDC]
    "0x7f1fa204bb700853D36994DA19F830b6Ad18455C": {
      marketTokenAddress: "0x7f1fa204bb700853D36994DA19F830b6Ad18455C",
      indexTokenAddress: "0xf97f4df75117a78c1A5a0DBb814Af92458539FB4",
      longTokenAddress: "0xf97f4df75117a78c1A5a0DBb814Af92458539FB4",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ARB/USD [ARB-USDC]
    "0xC25cEf6061Cf5dE5eb761b50E4743c1F5D7E5407": {
      marketTokenAddress: "0xC25cEf6061Cf5dE5eb761b50E4743c1F5D7E5407",
      indexTokenAddress: "0x912CE59144191C1204E64559FE8253a0e49E6548",
      longTokenAddress: "0x912CE59144191C1204E64559FE8253a0e49E6548",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SWAP-ONLY [USDC-USDC.e]
    "0x9C2433dFD71096C435Be9465220BB2B189375eA7": {
      marketTokenAddress: "0x9C2433dFD71096C435Be9465220BB2B189375eA7",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      shortTokenAddress: "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8",
    },
    // SWAP-ONLY [USDC-USDT]
    "0xB686BcB112660343E6d15BDb65297e110C8311c4": {
      marketTokenAddress: "0xB686BcB112660343E6d15BDb65297e110C8311c4",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      shortTokenAddress: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    },
    // SWAP-ONLY [USDC-DAI]
    "0xe2fEDb9e6139a182B98e7C2688ccFa3e9A53c665": {
      marketTokenAddress: "0xe2fEDb9e6139a182B98e7C2688ccFa3e9A53c665",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      shortTokenAddress: "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1",
    },
    // XRP/USD [WETH-USDC]
    "0x0CCB4fAa6f1F1B30911619f1184082aB4E25813c": {
      marketTokenAddress: "0x0CCB4fAa6f1F1B30911619f1184082aB4E25813c",
      indexTokenAddress: "0xc14e065b0067dE91534e032868f5Ac6ecf2c6868",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BNB/USD [BNB-USDC]
    "0x2d340912Aa47e33c90Efb078e69E70EFe2B34b9B": {
      marketTokenAddress: "0x2d340912Aa47e33c90Efb078e69E70EFe2B34b9B",
      indexTokenAddress: "0xa9004A5421372E1D83fB1f85b0fc986c912f91f3",
      longTokenAddress: "0xa9004A5421372E1D83fB1f85b0fc986c912f91f3",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AAVE [AAVE-USDC]
    "0x1CbBa6346F110c8A5ea739ef2d1eb182990e4EB2": {
      marketTokenAddress: "0x1CbBa6346F110c8A5ea739ef2d1eb182990e4EB2",
      indexTokenAddress: "0xba5DdD1f9d7F570dc94a51479a000E3BCE967196",
      longTokenAddress: "0xba5DdD1f9d7F570dc94a51479a000E3BCE967196",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ATOM [WETH-USDC]
    "0x248C35760068cE009a13076D573ed3497A47bCD4": {
      marketTokenAddress: "0x248C35760068cE009a13076D573ed3497A47bCD4",
      indexTokenAddress: "0x7D7F1765aCbaF847b9A1f7137FE8Ed4931FbfEbA",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // NEAR [WETH-USDC]
    "0x63Dc80EE90F26363B3FCD609007CC9e14c8991BE": {
      marketTokenAddress: "0x63Dc80EE90F26363B3FCD609007CC9e14c8991BE",
      indexTokenAddress: "0x1FF7F3EFBb9481Cbd7db4F932cBCD4467144237C",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AVAX [WAVAX-USDC]
    "0x7BbBf946883a5701350007320F525c5379B8178A": {
      marketTokenAddress: "0x7BbBf946883a5701350007320F525c5379B8178A",
      indexTokenAddress: "0x565609fAF65B92F7be02468acF86f8979423e514",
      longTokenAddress: "0x565609fAF65B92F7be02468acF86f8979423e514",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // OP [OP-USDC]
    "0x4fDd333FF9cA409df583f306B6F5a7fFdE790739": {
      marketTokenAddress: "0x4fDd333FF9cA409df583f306B6F5a7fFdE790739",
      indexTokenAddress: "0xaC800FD6159c2a2CB8fC31EF74621eB430287a5A",
      longTokenAddress: "0xaC800FD6159c2a2CB8fC31EF74621eB430287a5A",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BTC/USD [WBTC.e-WBTC.e]
    "0x7C11F78Ce78768518D743E81Fdfa2F860C6b9A77": {
      marketTokenAddress: "0x7C11F78Ce78768518D743E81Fdfa2F860C6b9A77",
      indexTokenAddress: "0x47904963fc8b2340414262125aF798B9655E58Cd",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
    },
    // ETH/USD [WETH-WETH]
    "0x450bb6774Dd8a756274E0ab4107953259d2ac541": {
      marketTokenAddress: "0x450bb6774Dd8a756274E0ab4107953259d2ac541",
      indexTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
    },
    // GMX/USD [GMX-USDC]
    "0x55391D178Ce46e7AC8eaAEa50A72D1A5a8A622Da": {
      marketTokenAddress: "0x55391D178Ce46e7AC8eaAEa50A72D1A5a8A622Da",
      indexTokenAddress: "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a",
      longTokenAddress: "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // PEPE [PEPE-USDC]
    "0x2b477989A149B17073D9C9C82eC9cB03591e20c6": {
      marketTokenAddress: "0x2b477989A149B17073D9C9C82eC9cB03591e20c6",
      indexTokenAddress: "0x25d887Ce7a35172C62FeBFD67a1856F20FaEbB00",
      longTokenAddress: "0x25d887Ce7a35172C62FeBFD67a1856F20FaEbB00",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // WIF [WIF-USDC]
    "0x0418643F94Ef14917f1345cE5C460C37dE463ef7": {
      marketTokenAddress: "0x0418643F94Ef14917f1345cE5C460C37dE463ef7",
      indexTokenAddress: "0xA1b91fe9FD52141Ff8cac388Ce3F10BFDc1dE79d",
      longTokenAddress: "0xA1b91fe9FD52141Ff8cac388Ce3F10BFDc1dE79d",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ETH/USD [wstETH-USDe]
    "0x0Cf1fb4d1FF67A3D8Ca92c9d6643F8F9be8e03E5": {
      marketTokenAddress: "0x0Cf1fb4d1FF67A3D8Ca92c9d6643F8F9be8e03E5",
      indexTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      longTokenAddress: "0x5979D7b546E38E414F7E9822514be443A4800529",
      shortTokenAddress: "0x5d3a1Ff2b6BAb83b63cd9AD0787074081a52ef34",
    },
    // SWAP-ONLY [wstETH-WETH]
    "0xb56E5E2eB50cf5383342914b0C85Fe62DbD861C8": {
      marketTokenAddress: "0xb56E5E2eB50cf5383342914b0C85Fe62DbD861C8",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0x5979D7b546E38E414F7E9822514be443A4800529",
      shortTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
    },
    // SWAP-ONLY [USDe-USDC]
    "0x45aD16Aaa28fb66Ef74d5ca0Ab9751F2817c81a4": {
      marketTokenAddress: "0x45aD16Aaa28fb66Ef74d5ca0Ab9751F2817c81a4",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0x5d3a1Ff2b6BAb83b63cd9AD0787074081a52ef34",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SHIB/USD [WETH-USDC]
    "0xB62369752D8Ad08392572db6d0cc872127888beD": {
      marketTokenAddress: "0xB62369752D8Ad08392572db6d0cc872127888beD",
      indexTokenAddress: "0x3E57D02f9d196873e55727382974b02EdebE6bfd",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // STX/USD [wBTC-USDC]
    "0xD9377d9B9a2327C7778867203deeA73AB8a68b6B": {
      marketTokenAddress: "0xD9377d9B9a2327C7778867203deeA73AB8a68b6B",
      indexTokenAddress: "0xBaf07cF91D413C0aCB2b7444B9Bf13b4e03c9D71",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ORDI/USD [wBTC-USDC]
    "0x93385F7C646A3048051914BDFaC25F4d620aeDF1": {
      marketTokenAddress: "0x93385F7C646A3048051914BDFaC25F4d620aeDF1",
      indexTokenAddress: "0x1E15d08f3CA46853B692EE28AE9C7a0b88a9c994",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BTC/USD [tBTC]
    "0xd62068697bCc92AF253225676D618B0C9f17C663": {
      marketTokenAddress: "0xd62068697bCc92AF253225676D618B0C9f17C663",
      indexTokenAddress: "0x47904963fc8b2340414262125aF798B9655E58Cd",
      longTokenAddress: "0x6c84a8f1c29108F47a79964b5Fe888D4f4D0dE40",
      shortTokenAddress: "0x6c84a8f1c29108F47a79964b5Fe888D4f4D0dE40",
    },
    //  EIGEN/USD [WETH-USDC]
    "0xD4b737892baB8446Ea1e8Bb901db092fb1EC1791": {
      marketTokenAddress: "0xD4b737892baB8446Ea1e8Bb901db092fb1EC1791",
      indexTokenAddress: "0x606C3e5075e5555e79Aa15F1E9FACB776F96C248",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    //  SATS/USD [WBTC-USDC]
    "0x8ea4Fb801493DaD8724F90Fb2e279534fa591366": {
      marketTokenAddress: "0x8ea4Fb801493DaD8724F90Fb2e279534fa591366",
      indexTokenAddress: "0x2cD2eB61D17b78239Fcd19aafF72981B5D5eF319",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // POL/USD [ETH-USDC]
    "0xD0a1AFDDE31Eb51e8b53bdCE989EB8C2404828a4": {
      marketTokenAddress: "0xD0a1AFDDE31Eb51e8b53bdCE989EB8C2404828a4",
      indexTokenAddress: "0x9c74772b713a1B032aEB173E28683D937E51921c",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AAVE/USD [ETH-USDC]
    "0x77B2eC357b56c7d05a87971dB0188DBb0C7836a5": {
      marketTokenAddress: "0x77B2eC357b56c7d05a87971dB0188DBb0C7836a5",
      indexTokenAddress: "0xba5DdD1f9d7F570dc94a51479a000E3BCE967196",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // PEPE/USD [ETH-USDC]
    "0x0Bb2a83F995E1E1eae9D7fDCE68Ab1ac55b2cc85": {
      marketTokenAddress: "0x0Bb2a83F995E1E1eae9D7fDCE68Ab1ac55b2cc85",
      indexTokenAddress: "0x25d887Ce7a35172C62FeBFD67a1856F20FaEbB00",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // UNI/USD [ETH-USDC]
    "0xD8471b9Ea126272E6d32B5e4782Ed76DB7E554a4": {
      marketTokenAddress: "0xD8471b9Ea126272E6d32B5e4782Ed76DB7E554a4",
      indexTokenAddress: "0xFa7F8980b0f1E64A2062791cc3b0871572f1F7f0",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // APE/USD [APE-USDC]
    "0xdAB21c4d1F569486334C93685Da2b3F9b0A078e8": {
      marketTokenAddress: "0xdAB21c4d1F569486334C93685Da2b3F9b0A078e8",
      indexTokenAddress: "0x7f9FBf9bDd3F4105C478b996B648FE6e828a1e98",
      longTokenAddress: "0x7f9FBf9bDd3F4105C478b996B648FE6e828a1e98",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SUI/USD [WETH-USDC]
    "0x6Ecf2133E2C9751cAAdCb6958b9654baE198a797": {
      marketTokenAddress: "0x6Ecf2133E2C9751cAAdCb6958b9654baE198a797",
      indexTokenAddress: "0x197aa2DE1313c7AD50184234490E12409B2a1f95",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SEI/USD [WETH-USDC]
    "0xB489711B1cB86afDA48924730084e23310EB4883": {
      marketTokenAddress: "0xB489711B1cB86afDA48924730084e23310EB4883",
      indexTokenAddress: "0x55e85A147a1029b985384822c0B2262dF8023452",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // APT/USD [WETH-USDC]
    "0x66A69c8eb98A7efE22A22611d1967dfec786a708": {
      marketTokenAddress: "0x66A69c8eb98A7efE22A22611d1967dfec786a708",
      indexTokenAddress: "0x3f8f0dCE4dCE4d0D1d0871941e79CDA82cA50d0B",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // TIA/USD [WETH-USDC]
    "0xBeB1f4EBC9af627Ca1E5a75981CE1AE97eFeDA22": {
      marketTokenAddress: "0xBeB1f4EBC9af627Ca1E5a75981CE1AE97eFeDA22",
      indexTokenAddress: "0x38676f62d166f5CE7De8433F51c6B3D6D9d66C19",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // TRX/USD [WETH-USDC]
    "0x3680D7bFE9260D3c5DE81AEB2194c119a59A99D1": {
      marketTokenAddress: "0x3680D7bFE9260D3c5DE81AEB2194c119a59A99D1",
      indexTokenAddress: "0xb06aa7E4af937C130dDade66f6ed7642716fe07A",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // TON/USD [WETH-USDC]
    "0x15c6eBD4175ffF9EE3c2615c556fCf62D2d9499c": {
      marketTokenAddress: "0x15c6eBD4175ffF9EE3c2615c556fCf62D2d9499c",
      indexTokenAddress: "0xB2f7cefaeEb08Aa347705ac829a7b8bE2FB560f3",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // WLD/USD [WETH-USDC]
    "0x872b5D567a2469Ed92D252eaCB0EB3BB0769e05b": {
      marketTokenAddress: "0x872b5D567a2469Ed92D252eaCB0EB3BB0769e05b",
      indexTokenAddress: "0x75B9AdD873641b253718810E6c65dB6d72311FD0",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BONK/USD [WETH-USDC]
    "0xFaC5fF56c269432706d47DC82Ab082E9AE7D989E": {
      marketTokenAddress: "0xFaC5fF56c269432706d47DC82Ab082E9AE7D989E",
      indexTokenAddress: "0x1FD10E767187A92f0AB2ABDEEF4505e319cA06B2",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // TAO/USD [WBTC-USDC]
    "0xe55e1A29985488A2c8846a91E925c2B7C6564db1": {
      marketTokenAddress: "0xe55e1A29985488A2c8846a91E925c2B7C6564db1",
      indexTokenAddress: "0x938aef36CAaFbcB37815251B602168087eC14648",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BOME/USD [WBTC/USDC]
    "0x71237F8C3d1484495A136022E16840b70fF84a69": {
      marketTokenAddress: "0x71237F8C3d1484495A136022E16840b70fF84a69",
      indexTokenAddress: "0x3Eea56A1ccCdbfB70A26aD381C71Ee17E4c8A15F",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // FLOKI/USD [WBTC/USDC]
    "0xfD46a5702D4d97cE0164375744c65F0c31A3901b": {
      marketTokenAddress: "0xfD46a5702D4d97cE0164375744c65F0c31A3901b",
      indexTokenAddress: "0x6792c5B8962ffbDD020c6b6FD0Be7b182e0e33a3",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MEME/USD [WBTC/USDC]
    "0x6CB901Cc64c024C3Fe4404c940FF9a3Acc229D2C": {
      marketTokenAddress: "0x6CB901Cc64c024C3Fe4404c940FF9a3Acc229D2C",
      indexTokenAddress: "0xaF770F03518686a365300ab35AD860e99967B2f0",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MEW/USD [WBTC/USDC]
    "0x71B7fF592a974e2B501D8A7a11f5c42DcD365244": {
      marketTokenAddress: "0x71B7fF592a974e2B501D8A7a11f5c42DcD365244",
      indexTokenAddress: "0x5503CF72f54b6d692d36BBCD391516A7dE068687",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // GMX [GMX]
    "0xbD48149673724f9cAeE647bb4e9D9dDaF896Efeb": {
      marketTokenAddress: "0xbD48149673724f9cAeE647bb4e9D9dDaF896Efeb",
      indexTokenAddress: "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a",
      longTokenAddress: "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a",
      shortTokenAddress: "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a",
    },
    // PENDLE/USD [PENDLE/USDC]
    "0x784292E87715d93afD7cb8C941BacaFAAA9A5102": {
      marketTokenAddress: "0x784292E87715d93afD7cb8C941BacaFAAA9A5102",
      indexTokenAddress: "0x0c880f6761F1af8d9Aa9C466984b80DAb9a8c9e8",
      longTokenAddress: "0x0c880f6761F1af8d9Aa9C466984b80DAb9a8c9e8",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ADA/USD [WBTC/USDC]
    "0xcaCb964144f9056A8f99447a303E60b4873Ca9B4": {
      marketTokenAddress: "0xcaCb964144f9056A8f99447a303E60b4873Ca9B4",
      indexTokenAddress: "0x53186c8419BEB83fE4Da74F7875041a1287337ED",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BCH/USD [WBTC/USDC]
    "0x62feB8Ec060A7dE5b32BbbF4AC70050f8a043C17": {
      marketTokenAddress: "0x62feB8Ec060A7dE5b32BbbF4AC70050f8a043C17",
      indexTokenAddress: "0xc33D9C096e74aa4f571E9417b69a19C4A1e72ef2",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // DOT/USD [WBTC/USDC]
    "0x7B2D09fca2395713dcc2F67323e4876F27b9ecB2": {
      marketTokenAddress: "0x7B2D09fca2395713dcc2F67323e4876F27b9ecB2",
      indexTokenAddress: "0xE958f107b467d5172573F761d26931D658C1b436",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ICP/USD [WBTC/USDC]
    "0xdc4e96A251Ff43Eeac710462CD8A9D18Dc802F18": {
      marketTokenAddress: "0xdc4e96A251Ff43Eeac710462CD8A9D18Dc802F18",
      indexTokenAddress: "0xdaf0A71608938F762e37eC5F72F670Cc44703454",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // XLM/USD [WBTC/USDC]
    "0xe902D1526c834D5001575b2d0Ef901dfD0aa097A": {
      marketTokenAddress: "0xe902D1526c834D5001575b2d0Ef901dfD0aa097A",
      indexTokenAddress: "0xc5dbD52Ae5a927Cf585B884011d0C7631C9974c6",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // RENDER/USD [WETH/USDC]
    "0x4c505e0062459cf8F60FfF13279c92ea15aE6e2D": {
      marketTokenAddress: "0x4c505e0062459cf8F60FfF13279c92ea15aE6e2D",
      indexTokenAddress: "0x82BB89fcc64c5d4016C5Ed1AB016bB0D1C20D6C3",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SOL/USD [SOL]
    "0xf22CFFA7B4174554FF9dBf7B5A8c01FaaDceA722": {
      marketTokenAddress: "0xf22CFFA7B4174554FF9dBf7B5A8c01FaaDceA722",
      indexTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
      longTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
      shortTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
    },
    // FIL/USD [WBTC/USDC]
    "0x262B5203f0fe00D9fe86ffecE01D0f54fC116180": {
      marketTokenAddress: "0x262B5203f0fe00D9fe86ffecE01D0f54fC116180",
      indexTokenAddress: "0x3AeBb98f57081DcBEb0B8EA823Cf84900A31e5D8",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // DYDX/USD [WBTC-USDC]
    "0x467C4A46287F6C4918dDF780D4fd7b46419c2291": {
      marketTokenAddress: "0x467C4A46287F6C4918dDF780D4fd7b46419c2291",
      indexTokenAddress: "0x0739Ad7AeA69aD36EdEb91b0e55cAC140427c632",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // INJ/USD [WBTC-USDC]
    "0x16466a03449CB9218EB6A980Aa4a44aaCEd27C25": {
      marketTokenAddress: "0x16466a03449CB9218EB6A980Aa4a44aaCEd27C25",
      indexTokenAddress: "0xfdE73EddbE6c5712A12B72c470F8FE5c77A7fF17",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // TRUMP/USD [ETH-USDC]
    "0xFec8f404FBCa3b11aFD3b3f0c57507C2a06dE636": {
      marketTokenAddress: "0xFec8f404FBCa3b11aFD3b3f0c57507C2a06dE636",
      indexTokenAddress: "0x30021aFA4767Ad66aA52A06dF8a5AB3acA9371fD",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MELANIA/USD [WETH-USDC]
    "0x12fD1A4BdB96219E637180Ff5293409502b2951D": {
      marketTokenAddress: "0x12fD1A4BdB96219E637180Ff5293409502b2951D",
      indexTokenAddress: "0xfa4F8E582214eBCe1A08eB2a65e08082053E441F",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ENA/USD [WETH-USDC]
    "0x9F159014CC218e942E9E9481742fE5BFa9ac5A2C": {
      marketTokenAddress: "0x9F159014CC218e942E9E9481742fE5BFa9ac5A2C",
      indexTokenAddress: "0xfe1Aac2CD9C5cC77b58EeCfE75981866ed0c8b7a",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AI16Z/USD [WBTC.e-USDC]
    "0xD60f1BA6a76979eFfE706BF090372Ebc0A5bF169": {
      marketTokenAddress: "0xD60f1BA6a76979eFfE706BF090372Ebc0A5bF169",
      indexTokenAddress: "0xBb69bd9dc152C2c0F083507641a46193d2B61EBb",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ANIME/USD [ANIME-USDC]
    "0x5707673D95a8fD317e2745C4217aCD64ca021B68": {
      marketTokenAddress: "0x5707673D95a8fD317e2745C4217aCD64ca021B68",
      indexTokenAddress: "0x37a645648dF29205C6261289983FB04ECD70b4B3",
      longTokenAddress: "0x37a645648dF29205C6261289983FB04ECD70b4B3",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // FARTCOIN/USD [WBTC.e-USDC]
    "0xe2730Ffe2136aA549327EBce93D58160df7821CB": {
      marketTokenAddress: "0xe2730Ffe2136aA549327EBce93D58160df7821CB",
      indexTokenAddress: "0xaca341E61aB6177B0b0Df46a612e4311F8a7605f",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BERA/USD [WETH-USDC]
    "0x876Ff160d63809674e03f82DC4D3C3Ae8B0acF28": {
      marketTokenAddress: "0x876Ff160d63809674e03f82DC4D3C3Ae8B0acF28",
      indexTokenAddress: "0x67ADABbAd211eA9b3B4E2fd0FD165E593De1e983",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // LDO/USD [WETH-USDC]
    "0xE61e608Ba010fF48A7dcE8eDd8B906744263d33E": {
      marketTokenAddress: "0xE61e608Ba010fF48A7dcE8eDd8B906744263d33E",
      indexTokenAddress: "0x9D678B4Dd38a6E01df8090aEB7974aD71142b05f",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // VIRTUAL/USD [WBTC-USDC]
    "0x75F190E0Be6E8B933A01423EFE398c6C721A5CfF": {
      marketTokenAddress: "0x75F190E0Be6E8B933A01423EFE398c6C721A5CfF",
      indexTokenAddress: "0xB6672496214C90134A9223894e709F26A5eED362",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // PENGU/USD [WBTC-USDC]
    "0x0c11Ed89889Fd03394E8d9d685cC5b85be569C99": {
      marketTokenAddress: "0x0c11Ed89889Fd03394E8d9d685cC5b85be569C99",
      indexTokenAddress: "0x4C1dac9b6eAf122Fe3DE824c1C2220413F3aC197",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ONDO/USD [WETH-USDC]
    "0xa8A455Ed94b315460CfF7d96966d91330f6A3bA0": {
      marketTokenAddress: "0xa8A455Ed94b315460CfF7d96966d91330f6A3bA0",
      indexTokenAddress: "0xEcFB4718aD19b626A77491895a2f99ea0cedEd08",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // FET/USD [WETH-USDC]
    "0x970e578fF01589Bb470CE38a2f1753152A009366": {
      marketTokenAddress: "0x970e578fF01589Bb470CE38a2f1753152A009366",
      indexTokenAddress: "0x83D5944E7f5EF1d8432002d3cb062e1012f6F8e6",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AIXBT/USD [WETH-USDC]
    "0x04DecfB37e46075189324817df80a32D22b9eD8D": {
      marketTokenAddress: "0x04DecfB37e46075189324817df80a32D22b9eD8D",
      indexTokenAddress: "0xcA543Cb8bCC76e4E0A034F56EB40a1029bDFd70E",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // S/USD [WBTC-USDC]
    "0x4d9bA415649c4B3c703562770C8ff3033478Cea1": {
      marketTokenAddress: "0x4d9bA415649c4B3c703562770C8ff3033478Cea1",
      indexTokenAddress: "0x8F6cCb99d4Fd0B4095915147b5ae3bbDb8075394",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // CAKE/USD [WBTC-USDC]
    "0xdE967676db7b1ccdBA2bD94B01B5b19DE4b563e4": {
      marketTokenAddress: "0xdE967676db7b1ccdBA2bD94B01B5b19DE4b563e4",
      indexTokenAddress: "0x580b373Ac16803BB0133356F470f3c7EEF54151B",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // HYPE/USD [WBTC-USDC]
    "0xBcb8FE13d02b023e8f94f6881Cc0192fd918A5C0": {
      marketTokenAddress: "0xBcb8FE13d02b023e8f94f6881Cc0192fd918A5C0",
      indexTokenAddress: "0xfDFA0A749dA3bCcee20aE0B4AD50E39B26F58f7C",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // JUP/USD [WBTC-USDC]
    "0x7DE8E1A1fbA845A330A6bD91118AfDA09610fB02": {
      marketTokenAddress: "0x7DE8E1A1fbA845A330A6bD91118AfDA09610fB02",
      indexTokenAddress: "0xfEd500Df379427Fbc48BDaf3b511b519c7eCCD26",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MKR/USD [WETH-USDC]
    "0x2aE5c5Cd4843cf588AA8D1289894318130acc823": {
      marketTokenAddress: "0x2aE5c5Cd4843cf588AA8D1289894318130acc823",
      indexTokenAddress: "0x8904De84c3bB3B7D2383F934Af40FcB3Ef82F28b",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // OM/USD [WBTC-USDC]
    "0x89EB78679921499632fF16B1be3ee48295cfCD91": {
      marketTokenAddress: "0x89EB78679921499632fF16B1be3ee48295cfCD91",
      indexTokenAddress: "0x1f3407Ea067DfBDF6dEb6bBFdA4869215fB0ab77",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // DOLO/USD [WETH-USDC]
    "0x4D3Eb91efd36C2b74181F34B111bc1E91a0d0cb4": {
      marketTokenAddress: "0x4D3Eb91efd36C2b74181F34B111bc1E91a0d0cb4",
      indexTokenAddress: "0x97Ce1F309B949f7FBC4f58c5cb6aa417A5ff8964",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ZRO/USD [WETH-USDC]
    "0x9e79146b3A022Af44E0708c6794F03Ef798381A5": {
      marketTokenAddress: "0x9e79146b3A022Af44E0708c6794F03Ef798381A5",
      indexTokenAddress: "0xa8193C55C34Ed22e1Dbe73FD5Adc668E51578a67",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // CRV/USD - [WETH-USDC]
    "0x0e46941F9bfF8d0784BFfa3d0D7883CDb82D7aE7": {
      marketTokenAddress: "0x0e46941F9bfF8d0784BFfa3d0D7883CDb82D7aE7",
      indexTokenAddress: "0xe5f01aeAcc8288E9838A60016AB00d7b6675900b",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MOODENG/USD [WBTC-USDC]
    "0x2523B89298908FEf4c5e5bd6F55F20926e22058f": {
      marketTokenAddress: "0x2523B89298908FEf4c5e5bd6F55F20926e22058f",
      indexTokenAddress: "0xd3898c6570974AEca38a8ACf22fd60739e528A99",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // XMR/USD [WBTC-USDC]
    "0x7c54D547FAD72f8AFbf6E5b04403A0168b654C6f": {
      marketTokenAddress: "0x7c54D547FAD72f8AFbf6E5b04403A0168b654C6f",
      indexTokenAddress: "0x13674172E6E44D31d4bE489d5184f3457c40153A",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // PI/USD [WBTC-USDC]
    "0x39AC3C494950A4363D739201BA5A0861265C9ae5": {
      marketTokenAddress: "0x39AC3C494950A4363D739201BA5A0861265C9ae5",
      indexTokenAddress: "0xd1738d37401a0A71f7E382d2cFeCD3ab69687017",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // PUMP/USD [WBTC-USDC]
    "0x4C0Bb704529Fa49A26bD854802d70206982c6f1B": {
      marketTokenAddress: "0x4C0Bb704529Fa49A26bD854802d70206982c6f1B",
      indexTokenAddress: "0x9c060B2fA953b5f69879a8B7B81f62BFfEF360be",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SPX6900/USD [WETH-USDC]
    "0x8263bC3766a09f6dD4Bab04b4bf8D45F2B0973FF": {
      marketTokenAddress: "0x8263bC3766a09f6dD4Bab04b4bf8D45F2B0973FF",
      indexTokenAddress: "0xb736be525A65326513351058427d1f47B0CfB045",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MNT/USD [WETH-USDC]
    "0x40dAEAc02dCf6b3c51F9151f532C21DCEF2F7E63": {
      marketTokenAddress: "0x40dAEAc02dCf6b3c51F9151f532C21DCEF2F7E63",
      indexTokenAddress: "0x955cd91eEaE618F5a7b49E1e3c7482833B10DAb4",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ARB/USD [ARB-ARB]
    "0x672fEA44f4583DdaD620d60C1Ac31021F47558Cb": {
      marketTokenAddress: "0x672fEA44f4583DdaD620d60C1Ac31021F47558Cb",
      indexTokenAddress: "0x912CE59144191C1204E64559FE8253a0e49E6548",
      longTokenAddress: "0x912CE59144191C1204E64559FE8253a0e49E6548",
      shortTokenAddress: "0x912CE59144191C1204E64559FE8253a0e49E6548",
    },
    // ALGO/USD [WBTC-USDC]
    "0x3B7f4e4Cf2fa43df013d2B32673e6A01d29ab2Ac": {
      marketTokenAddress: "0x3B7f4e4Cf2fa43df013d2B32673e6A01d29ab2Ac",
      indexTokenAddress: "0x72Cd3a21aA7A898028d9501868Fbe6dED0020434",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // CRO/USD [WBTC-USDC]
    "0xa29FfE4152B65A0347512Ae5c6A4Bbc7a3d6d51B": {
      marketTokenAddress: "0xa29FfE4152B65A0347512Ae5c6A4Bbc7a3d6d51B",
      indexTokenAddress: "0xB7EfE7c7f059E84Ab87A83A169c583Fb4A54fAc3",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // HBAR/USD [WBTC-USDC]
    "0x9f0849FB830679829d1FB759b11236D375D15C78": {
      marketTokenAddress: "0x9f0849FB830679829d1FB759b11236D375D15C78",
      indexTokenAddress: "0xEb2A83b973f4dbB9511D92dd40d2ba4C683f0971",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // CVX/USD [WETH-USDC]
    "0x41E3bC5B72384C8B26b559B7d16C2B81Fd36fbA2": {
      marketTokenAddress: "0x41E3bC5B72384C8B26b559B7d16C2B81Fd36fbA2",
      indexTokenAddress: "0x3B6f801C0052Dfe0Ac80287D611F31B7c47B9A6b",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // KAS/USD [WBTC-USDC]
    "0x4024418592450E4d62faB15e2f833FC03A3447dc": {
      marketTokenAddress: "0x4024418592450E4d62faB15e2f833FC03A3447dc",
      indexTokenAddress: "0x91c6a8F6aFAC036F4ABf1bA55f4E76892E865E4a",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // OKB/USD [WETH-USDC]
    "0x2a331e51a3D17211852d8625a1029898450e539B": {
      marketTokenAddress: "0x2a331e51a3D17211852d8625a1029898450e539B",
      indexTokenAddress: "0xd37F01A3379f052FEF70F63c0Be27931891aa2B9",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // WIF/USD [WBTC-USDC]
    "0x3f649eab7f4CE4945F125939C64429Be2C5d0cB4": {
      marketTokenAddress: "0x3f649eab7f4CE4945F125939C64429Be2C5d0cB4",
      indexTokenAddress: "0xA1b91fe9FD52141Ff8cac388Ce3F10BFDc1dE79d",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AERO/USD [WETH-USDC]
    "0xfaEaE570B07618D3F10360608E43c241181c4614": {
      marketTokenAddress: "0xfaEaE570B07618D3F10360608E43c241181c4614",
      indexTokenAddress: "0xEcc5eb985Ddbb8335b175b0A2A1144E4c978F1f6",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BRETT/USD [WETH-USDC]
    "0x6EeE8098dBC106aEde99763FA5F955A5bBc42C50": {
      marketTokenAddress: "0x6EeE8098dBC106aEde99763FA5F955A5bBc42C50",
      indexTokenAddress: "0x4249F6e0808bEfF7368AaAD3F7A3Fd511F61Ee60",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // WLFI/USD [WETH-USDC]
    "0xb3588455858a49D3244237CEe00880CcB84b91Dd": {
      marketTokenAddress: "0xb3588455858a49D3244237CEe00880CcB84b91Dd",
      indexTokenAddress: "0xC5799ab6E2818fD8d0788dB8D156B0c5db1Bf97b",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // LINK/USD [WETH-USDC]
    "0xF913B4748031EF569898ED91e5BA0d602bB93298": {
      marketTokenAddress: "0xF913B4748031EF569898ED91e5BA0d602bB93298",
      indexTokenAddress: "0xf97f4df75117a78c1A5a0DBb814Af92458539FB4",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // MORPHO/USD [WETH-USDC]
    "0x4De268aC68477f794C3eAC5A419Cbcffc2cD5e02": {
      marketTokenAddress: "0x4De268aC68477f794C3eAC5A419Cbcffc2cD5e02",
      indexTokenAddress: "0xF67b2a901D674B443Fa9f6DB2A689B37c07fD4fE",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // VVV/USD [WETH-USDC]
    "0x947C521E44f727219542B0f91a85182193c1D2ad": {
      marketTokenAddress: "0x947C521E44f727219542B0f91a85182193c1D2ad",
      indexTokenAddress: "0xB79Eb5BA64A167676694bB41bc1640F95d309a2F",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // WELL/USD [WETH-USDC]
    "0x2347EbB8645Cc2EA0Ba92D1EC59704031F2fCCf4": {
      marketTokenAddress: "0x2347EbB8645Cc2EA0Ba92D1EC59704031F2fCCf4",
      indexTokenAddress: "0x465A31E5bA29b8EAcC860d499D714a6f07e56E85",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // KTA/USD [WETH-USDC]
    "0x970b730b5dD18de53A230eE8F4af088dBC3a6F8d": {
      marketTokenAddress: "0x970b730b5dD18de53A230eE8F4af088dBC3a6F8d",
      indexTokenAddress: "0x96Ee343E36E8642627FAEa235D57a9FEC8a6e34f",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ZORA/USD [WETH-USDC]
    "0xac484106d935f0f20F1485b631fA6F65AeEff550": {
      marketTokenAddress: "0xac484106d935f0f20F1485b631fA6F65AeEff550",
      indexTokenAddress: "0xc5ff0eB026dB972F95DF3dfF04e697d8b660092a",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // XPL/USD [WBTC.e-USDC]
    "0x4b67aa8F754b17b1029Ad2DB4fb6a276CCe350c4": {
      marketTokenAddress: "0x4b67aa8F754b17b1029Ad2DB4fb6a276CCe350c4",
      indexTokenAddress: "0x2e73bDBee83D91623736D514b0BB41f2afd9C7Fd",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // ASTER/USD [WBTC-USDC]
    "0x0164B6c847c65e07C9F6226149ADBFA7C1dE40Cf": {
      marketTokenAddress: "0x0164B6c847c65e07C9F6226149ADBFA7C1dE40Cf",
      indexTokenAddress: "0x2aAB60E62f05d17e58dEc982870bfAdc7F4e7ADF",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // 0G/USD [WBTC-USDC]
    "0xe024188850A822409F362209C1EF2cFdc7c4DE4C": {
      marketTokenAddress: "0xe024188850A822409F362209C1EF2cFdc7c4DE4C",
      indexTokenAddress: "0x95c317066CF214b2E6588B2685D949384504F51e",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // AVNT/USD [WETH-USDC]
    "0xCEff9D261A96Cb78Df35f9333ba9F2F4CFcb8a68": {
      marketTokenAddress: "0xCEff9D261A96Cb78Df35f9333ba9F2F4CFcb8a68",
      indexTokenAddress: "0xdB58EB7f408EbA2176eCb44A4696292605cCEB39",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // LINEA/USD [WETH-USDC]
    "0x6d9430A116ed4d4FC6FE1996A5493662d555b07E": {
      marketTokenAddress: "0x6d9430A116ed4d4FC6FE1996A5493662d555b07E",
      indexTokenAddress: "0xc4017CFe7D7eaBDE63d3252caBF26A286fE2B1E0",
      longTokenAddress: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // SOL/USD [WBTC.e-USDC]
    "0xcf083d35AD306A042d4Fb312fCdd8228b52b82f8": {
      marketTokenAddress: "0xcf083d35AD306A042d4Fb312fCdd8228b52b82f8",
      indexTokenAddress: "0x2bcC6D6CdBbDC0a4071e48bb3B969b06B3330c07",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
    // BNB/USD [WBTC.e-USDC]
    "0x065577D05c3D4C11505ed7bc97BBF85d462A6A6f": {
      marketTokenAddress: "0x065577D05c3D4C11505ed7bc97BBF85d462A6A6f",
      indexTokenAddress: "0xa9004A5421372E1D83fB1f85b0fc986c912f91f3",
      longTokenAddress: "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f",
      shortTokenAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    },
  },
  [AVALANCHE]: {
    // BTC/USD [BTC-USDC]
    "0xFb02132333A79C8B5Bd0b64E3AbccA5f7fAf2937": {
      marketTokenAddress: "0xFb02132333A79C8B5Bd0b64E3AbccA5f7fAf2937",
      indexTokenAddress: "0x152b9d0FdC40C096757F570A51E494bd4b943E50",
      longTokenAddress: "0x152b9d0FdC40C096757F570A51E494bd4b943E50",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // ETH/USD [ETH-USDC]
    "0xB7e69749E3d2EDd90ea59A4932EFEa2D41E245d7": {
      marketTokenAddress: "0xB7e69749E3d2EDd90ea59A4932EFEa2D41E245d7",
      indexTokenAddress: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
      longTokenAddress: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // DOGE/USD [WAVAX-USDC]
    "0x8970B527E84aA17a33d38b65e9a5Ab5817FC0027": {
      marketTokenAddress: "0x8970B527E84aA17a33d38b65e9a5Ab5817FC0027",
      indexTokenAddress: "0xC301E6fe31062C557aEE806cc6A841aE989A3ac6",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // SOL/USD [SOL-USDC]
    "0xd2eFd1eA687CD78c41ac262B3Bc9B53889ff1F70": {
      marketTokenAddress: "0xd2eFd1eA687CD78c41ac262B3Bc9B53889ff1F70",
      indexTokenAddress: "0xFE6B19286885a4F7F55AdAD09C3Cd1f906D2478F",
      longTokenAddress: "0xFE6B19286885a4F7F55AdAD09C3Cd1f906D2478F",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // LTC/USD [WAVAX-USDC]
    "0xA74586743249243D3b77335E15FE768bA8E1Ec5A": {
      marketTokenAddress: "0xA74586743249243D3b77335E15FE768bA8E1Ec5A",
      indexTokenAddress: "0x8E9C35235C38C44b5a53B56A41eaf6dB9a430cD6",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // AVAX/USD [WAVAX-USDC]
    "0x913C1F46b48b3eD35E7dc3Cf754d4ae8499F31CF": {
      marketTokenAddress: "0x913C1F46b48b3eD35E7dc3Cf754d4ae8499F31CF",
      indexTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // SWAP-ONLY [USDC-USDT.e]
    "0xf3652Eba45DC761e7ADd4091627d5Cda21F61613": {
      marketTokenAddress: "0xf3652Eba45DC761e7ADd4091627d5Cda21F61613",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
      shortTokenAddress: "0xc7198437980c041c805A1EDcbA50c1Ce5db95118",
    },
    // SWAP-ONLY [USDC-USDC.e]
    "0x297e71A931C5825867E8Fb937Ae5cda9891C2E99": {
      marketTokenAddress: "0x297e71A931C5825867E8Fb937Ae5cda9891C2E99",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
      shortTokenAddress: "0xA7D7079b0FEaD91F3e65f86E8915Cb59c1a4C664",
    },
    // SWAP-ONLY [USDT-USDT.e]
    "0xA7b768d6a1f746fd5a513D440DF2970ff099B0fc": {
      marketTokenAddress: "0xA7b768d6a1f746fd5a513D440DF2970ff099B0fc",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7",
      shortTokenAddress: "0xc7198437980c041c805A1EDcbA50c1Ce5db95118",
    },
    // SWAP-ONLY [USDC-DAI.e]
    "0xDf8c9BD26e7C1A331902758Eb013548B2D22ab3b": {
      marketTokenAddress: "0xDf8c9BD26e7C1A331902758Eb013548B2D22ab3b",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
      shortTokenAddress: "0xd586E7F844cEa2F87f50152665BCbc2C279D8d70",
    },
    // XRP/USD [WAVAX-USDC]
    "0xD1cf931fa12783c1dd5AbB77a0706c27CF352f25": {
      marketTokenAddress: "0xD1cf931fa12783c1dd5AbB77a0706c27CF352f25",
      indexTokenAddress: "0x34B2885D617cE2ddeD4F60cCB49809fc17bb58Af",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // BTC/USD [BTC-BTC]
    "0x3ce7BCDB37Bf587d1C17B930Fa0A7000A0648D12": {
      marketTokenAddress: "0x3ce7BCDB37Bf587d1C17B930Fa0A7000A0648D12",
      indexTokenAddress: "0x152b9d0FdC40C096757F570A51E494bd4b943E50",
      longTokenAddress: "0x152b9d0FdC40C096757F570A51E494bd4b943E50",
      shortTokenAddress: "0x152b9d0FdC40C096757F570A51E494bd4b943E50",
    },
    // ETH/USD [ETH-ETH]
    "0x2A3Cf4ad7db715DF994393e4482D6f1e58a1b533": {
      marketTokenAddress: "0x2A3Cf4ad7db715DF994393e4482D6f1e58a1b533",
      indexTokenAddress: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
      longTokenAddress: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
      shortTokenAddress: "0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB",
    },
    // AVAX/USD [AVAX-AVAX]
    "0x08b25A2a89036d298D6dB8A74ace9d1ce6Db15E5": {
      marketTokenAddress: "0x08b25A2a89036d298D6dB8A74ace9d1ce6Db15E5",
      indexTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
    },
    // TRUMP/USD [WAVAX-USDC]
    "0xfB626c4e3E153947A6A94041814c25E449064dAD": {
      marketTokenAddress: "0xfB626c4e3E153947A6A94041814c25E449064dAD",
      indexTokenAddress: "0x2f6d7be53fab5538065a226BA091015d422a7528",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // MELANIA/USD [WAVAX-USDC]
    "0xe19da27Bf9733c429445E289B662bECDCa6ce10b": {
      marketTokenAddress: "0xe19da27Bf9733c429445E289B662bECDCa6ce10b",
      indexTokenAddress: "0xd42C991a4FAb293C57a7bf25C2E2ec5aE1dB1714",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // PUMP/USD [WAVAX-USDC]
    "0x94cE6F65188a92F297C7f0A5A7B3cAd9013450F8": {
      marketTokenAddress: "0x94cE6F65188a92F297C7f0A5A7B3cAd9013450F8",
      indexTokenAddress: "0xdA598795DfE56388ca3D35e2ccFA96EFf83eC306",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // WLFI/USD [WAVAX-USDC]
    "0x1cb9932CE322877A2B86489BD1aA3C3CfF879F0d": {
      marketTokenAddress: "0x1cb9932CE322877A2B86489BD1aA3C3CfF879F0d",
      indexTokenAddress: "0xbDF8a77ACB7A54597E7760b34D3E632912bB59b7",
      longTokenAddress: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
      shortTokenAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
    },
    // XAUt0/USD [XAUt0-XAUt0]
    "0x1635eF7FBdce68eC80A3672aB710A5a99044f5c9": {
      marketTokenAddress: "0x1635eF7FBdce68eC80A3672aB710A5a99044f5c9",
      indexTokenAddress: "0x2775d5105276781B4b85bA6eA6a6653bEeD1dd32",
      longTokenAddress: "0x2775d5105276781B4b85bA6eA6a6653bEeD1dd32",
      shortTokenAddress: "0x2775d5105276781B4b85bA6eA6a6653bEeD1dd32",
    },
    // XAUt0/USD [XAUt0-USDT]
    "0x92d3DA41E166A12e3Ede9e2Dd9A272C5c6FC55E1": {
      marketTokenAddress: "0x92d3DA41E166A12e3Ede9e2Dd9A272C5c6FC55E1",
      indexTokenAddress: "0x2775d5105276781B4b85bA6eA6a6653bEeD1dd32",
      longTokenAddress: "0x2775d5105276781B4b85bA6eA6a6653bEeD1dd32",
      shortTokenAddress: "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7",
    },
  },
  [AVALANCHE_FUJI]: {
    // AVAX/USD [WAVAX-USDC]
    "0xD996ff47A1F763E1e55415BC4437c59292D1F415": {
      marketTokenAddress: "0xD996ff47A1F763E1e55415BC4437c59292D1F415",
      indexTokenAddress: "0x1D308089a2D1Ced3f1Ce36B1FcaF815b07217be3",
      longTokenAddress: "0x1D308089a2D1Ced3f1Ce36B1FcaF815b07217be3",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // ETH/USD [ETH-USDC]
    "0xbf338a6C595f06B7Cfff2FA8c958d49201466374": {
      marketTokenAddress: "0xbf338a6C595f06B7Cfff2FA8c958d49201466374",
      indexTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // ETH/USD [ETH-DAI]
    "0xDdF708B284C5C26BE67Adf9C51DFa935b5035bF8": {
      marketTokenAddress: "0xDdF708B284C5C26BE67Adf9C51DFa935b5035bF8",
      indexTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // ETH/USD [USDC]
    "0xe28323955C05B75E25B56C1c996C1354Eb5Aa13D": {
      marketTokenAddress: "0xe28323955C05B75E25B56C1c996C1354Eb5Aa13D",
      indexTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      longTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // WBTC/USD [WBTC-USDC]
    "0x79E6e0E454dE82fA98c02dB012a2A69103630B07": {
      marketTokenAddress: "0x79E6e0E454dE82fA98c02dB012a2A69103630B07",
      indexTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      longTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // WBTC/USD [WBTC-DAI]
    "0x4b6ccF6E429f038087A26b13DD6ab4304F7E5DF1": {
      marketTokenAddress: "0x4b6ccF6E429f038087A26b13DD6ab4304F7E5DF1",
      indexTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      longTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // SOL/USD [ETH-USDC]
    "0xEDF9Be35bE84cD1e39Bda59Bd7ae8A704C12e06f": {
      marketTokenAddress: "0xEDF9Be35bE84cD1e39Bda59Bd7ae8A704C12e06f",
      indexTokenAddress: "0x137f4a7336df4f3f11894718528516edaaD0B082",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // SWAP-ONLY [USDC-USDT]
    "0xeE8827D67C054cAa89C9d6058Fdddccd1C499c74": {
      marketTokenAddress: "0xeE8827D67C054cAa89C9d6058Fdddccd1C499c74",
      indexTokenAddress: "0x0000000000000000000000000000000000000000",
      longTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
      shortTokenAddress: "0x50df4892Bd13f01E4e1Cd077ff394A8fa1A3fD7c",
    },
    // DOGE/USD [ETH-DAI]
    "0xAC2c6C1b0cd1CabF78B4e8ad58aA9d43375318Cb": {
      marketTokenAddress: "0xAC2c6C1b0cd1CabF78B4e8ad58aA9d43375318Cb",
      indexTokenAddress: "0x2265F317eA5f47A684E5B26c50948617c945d986",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // LINK/USD [ETH-DAI]
    "0xeDf53322e288F597436f5d5849771662AEe16A1C": {
      marketTokenAddress: "0xeDf53322e288F597436f5d5849771662AEe16A1C",
      indexTokenAddress: "0x6BD09E8D65AD5cc761DF62454452d4EC1545e647",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // BNB/USD [ETH-DAI]
    "0x017de90B0fa830C592805C6148c089191716f04c": {
      marketTokenAddress: "0x017de90B0fa830C592805C6148c089191716f04c",
      indexTokenAddress: "0x110892Dd5fa73bE430c0ade694febD9a4CAc68Be",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // ADA/USD [ETH-DAI]
    "0x695a07d3DD551b0E77A348cC6A873c1eb183FA98": {
      marketTokenAddress: "0x695a07d3DD551b0E77A348cC6A873c1eb183FA98",
      indexTokenAddress: "0xE64dfFF37Fa6Fe969b792B4146cEe2774Ef6e1a1",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // TRX/USD [ETH-DAI]
    "0x927f31364b8836021e4F73B27a5d0EbB35C74679": {
      marketTokenAddress: "0x927f31364b8836021e4F73B27a5d0EbB35C74679",
      indexTokenAddress: "0x0D1495527C255068F2f6feE31C85d326D0A76FE8",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // MATIC/USD [ETH-USDC]
    "0x62408de4cB1a499842EC53296EF8dD99A825CcEb": {
      marketTokenAddress: "0x62408de4cB1a499842EC53296EF8dD99A825CcEb",
      indexTokenAddress: "0xadc4698B257F78187Fd675FBf591a09f4c975240",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // DOT/USD [ETH-USDC]
    "0xCc6AC193E1d1Ef102eCBBA864BBfE87E414a7A0D": {
      marketTokenAddress: "0xCc6AC193E1d1Ef102eCBBA864BBfE87E414a7A0D",
      indexTokenAddress: "0x65FFb5664a7B3377A5a27D9e59C72Fb1A5E94962",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // UNI/USD [ETH-USDC]
    "0xE446E8f7074c0A97bb7cd448fA2CC3346045F514": {
      marketTokenAddress: "0xE446E8f7074c0A97bb7cd448fA2CC3346045F514",
      indexTokenAddress: "0xF62dC1d2452d0893735D22945Af53C290b158eAF",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // TEST/USD [ETH-USDC]
    "0x1d9dC405CCEFA78b203BaD9CCe1b1623D2B25D9e": {
      marketTokenAddress: "0x1d9dC405CCEFA78b203BaD9CCe1b1623D2B25D9e",
      indexTokenAddress: "0x42DD131E1086FFCc59bAE9498D71E20E0C889B14",
      longTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      shortTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
    },
    // WBTC/USD [USDC-USDT]
    "0xd783EB54407d6d3A4D5c94b634eC9BAE3F574098": {
      marketTokenAddress: "0xd783EB54407d6d3A4D5c94b634eC9BAE3F574098",
      indexTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      longTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
      shortTokenAddress: "0x50df4892Bd13f01E4e1Cd077ff394A8fa1A3fD7c",
    },
    // ETH/USD [USDC-DAI]
    "0x6d72D2787107c32a48bbA4687Eb8F9C19FE5e29C": {
      marketTokenAddress: "0x6d72D2787107c32a48bbA4687Eb8F9C19FE5e29C",
      indexTokenAddress: "0x82F0b3695Ed2324e55bbD9A9554cB4192EC3a514",
      longTokenAddress: "0x3eBDeaA0DB3FfDe96E7a0DBBAFEC961FC50F725F",
      shortTokenAddress: "0x51290cb93bE5062A6497f16D9cd3376Adf54F920",
    },
    // WBTC/USD [WBTC]
    "0x3b649015Fe0a4d15617e57aA11c0FbbfA03A9e11": {
      marketTokenAddress: "0x3b649015Fe0a4d15617e57aA11c0FbbfA03A9e11",
      indexTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      longTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
      shortTokenAddress: "0x3Bd8e00c25B12E6E60fc8B6f1E1E2236102073Ca",
    },
  },
  [ARBITRUM_SEPOLIA]: {
    // ETH/USD [WETH-USDC]
    "0x482Df3D320C964808579b585a8AC7Dd5D144eFaF": {
      marketTokenAddress: "0x482Df3D320C964808579b585a8AC7Dd5D144eFaF",
      indexTokenAddress: "0x980B62Da83eFf3D4576C647993b0c1D7faf17c73",
      longTokenAddress: "0x980B62Da83eFf3D4576C647993b0c1D7faf17c73",
      shortTokenAddress: "0x3321Fd36aEaB0d5CdfD26f4A3A93E2D2aAcCB99f",
    },
    // BTC/USD [BTC-USDC]
    "0xBb532Ab4923C23c2bfA455151B14fec177a34C0D": {
      marketTokenAddress: "0xBb532Ab4923C23c2bfA455151B14fec177a34C0D",
      indexTokenAddress: "0xF79cE1Cf38A09D572b021B4C5548b75A14082F12",
      longTokenAddress: "0xF79cE1Cf38A09D572b021B4C5548b75A14082F12",
      shortTokenAddress: "0x3321Fd36aEaB0d5CdfD26f4A3A93E2D2aAcCB99f",
    },
    // ETH/USD [WETH-USDC.SG]
    "0xb6fC4C9eB02C35A134044526C62bb15014Ac0Bcc": {
      marketTokenAddress: "0xb6fC4C9eB02C35A134044526C62bb15014Ac0Bcc",
      indexTokenAddress: "0x980B62Da83eFf3D4576C647993b0c1D7faf17c73",
      longTokenAddress: "0x980B62Da83eFf3D4576C647993b0c1D7faf17c73",
      shortTokenAddress: "0x3253a335E7bFfB4790Aa4C25C4250d206E9b9773",
    },
    // BTC/USD [BTC-USDC.SG]
    "0x3A83246bDDD60c4e71c91c10D9A66Fd64399bBCf": {
      marketTokenAddress: "0x3A83246bDDD60c4e71c91c10D9A66Fd64399bBCf",
      indexTokenAddress: "0xF79cE1Cf38A09D572b021B4C5548b75A14082F12",
      longTokenAddress: "0xF79cE1Cf38A09D572b021B4C5548b75A14082F12",
      shortTokenAddress: "0x3253a335E7bFfB4790Aa4C25C4250d206E9b9773",
    },
    // CRV/USD [WETH-USDC]
    "0xAde9D177B9E060D2064ee9F798125e6539fDaA1c": {
      marketTokenAddress: "0xAde9D177B9E060D2064ee9F798125e6539fDaA1c",
      indexTokenAddress: "0xD5DdAED48B09fa1D7944bd662CB05265FCD7077C",
      longTokenAddress: "0x980B62Da83eFf3D4576C647993b0c1D7faf17c73",
      shortTokenAddress: "0x3253a335E7bFfB4790Aa4C25C4250d206E9b9773",
    },
  },
  [BOTANIX]: {
    // BTC/USD [stBTC-stBTC]
    "0x6682BB60590a045A956541B1433f016Ed22E361d": {
      marketTokenAddress: "0x6682BB60590a045A956541B1433f016Ed22E361d",
      indexTokenAddress: "0x1B9e25f54225bcdCf347569E38C41Ade9BB686e5",
      longTokenAddress: "0xF4586028FFdA7Eca636864F80f8a3f2589E33795",
      shortTokenAddress: "0xF4586028FFdA7Eca636864F80f8a3f2589E33795",
    },
    // BTC/USD [stBTC-USDC.E]
    "0x2f95a2529328E427d3204555F164B1102086690E": {
      marketTokenAddress: "0x2f95a2529328E427d3204555F164B1102086690E",
      indexTokenAddress: "0x1B9e25f54225bcdCf347569E38C41Ade9BB686e5",
      longTokenAddress: "0xF4586028FFdA7Eca636864F80f8a3f2589E33795",
      shortTokenAddress: "0x29eE6138DD4C9815f46D34a4A1ed48F46758A402",
    },
    // BTC/USD [PBTC-PBTC]
    "0x6bFDD025827F7CE130BcfC446927AEF34ae2a98d": {
      marketTokenAddress: "0x6bFDD025827F7CE130BcfC446927AEF34ae2a98d",
      indexTokenAddress: "0x1B9e25f54225bcdCf347569E38C41Ade9BB686e5",
      longTokenAddress: "0x0D2437F93Fed6EA64Ef01cCde385FB1263910C56",
      shortTokenAddress: "0x0D2437F93Fed6EA64Ef01cCde385FB1263910C56",
    },
  },
};

export type MarketLabel = `${string}/USD [${string}-${string}]`;

export function getMarketsByChainId(chainId: ContractsChainId): Record<string, MarketConfig> {
  if (!MARKETS[chainId]) {
    throw new Error(`Markets not found for chainId ${chainId}`);
  }

  return MARKETS[chainId];
}

export function getMarketByLabel(chainId: ContractsChainId, label: MarketLabel): MarketConfig {
  const marketsByAddress = MARKETS[chainId];

  if (!marketsByAddress) {
    throw new Error(`Markets not found for chainId ${chainId}`);
  }

  const labelMatch = label.match(/^(.+?)\/USD\s*\[([^\]]+)\]$/i);

  if (!labelMatch) {
    throw new Error(`Invalid market label ${label}`);
  }

  const [, indexSymbolRaw, tokensPart] = labelMatch;

  const separatorIndex = tokensPart.search(/[-/]/);

  if (separatorIndex === -1) {
    throw new Error(`Invalid market label ${label}`);
  }

  const longSymbolRaw = tokensPart.slice(0, separatorIndex).trim();
  const shortSymbolRaw = tokensPart.slice(separatorIndex + 1).trim();

  if (!longSymbolRaw || !shortSymbolRaw) {
    throw new Error(`Invalid market label ${label}`);
  }

  const indexToken = getTokenBySymbol(chainId, fixTokenSymbolFromMarketLabel(chainId, indexSymbolRaw));
  const longToken = getTokenBySymbol(chainId, fixTokenSymbolFromMarketLabel(chainId, longSymbolRaw), {
    isSynthetic: false,
  });
  const shortToken = getTokenBySymbol(chainId, fixTokenSymbolFromMarketLabel(chainId, shortSymbolRaw), {
    isSynthetic: false,
  });

  if (!longToken || !shortToken || !indexToken) {
    throw new Error(`Invalid market label ${label}`);
  }

  const market = Object.values(marketsByAddress).find(
    (market) =>
      market.longTokenAddress === longToken.address &&
      market.shortTokenAddress === shortToken.address &&
      market.indexTokenAddress === indexToken.address
  );

  if (!market) {
    throw new Error(`Market ${label} not found`);
  }

  return market;
}

export const fixTokenSymbolFromMarketLabel = (chainId: ContractsChainId, symbol: string) => {
  if (chainId === ARBITRUM && symbol === "WBTC") {
    return "BTC";
  }
  if (chainId === ARBITRUM && symbol === "ETH") {
    return "WETH";
  }
  return symbol;
};

export function isMarketTokenAddress(chainId: number, marketTokenAddress: string): boolean {
  return Boolean(MARKETS[chainId]?.[marketTokenAddress]);
}

export function getTokenAddressByMarket<T extends "long" | "short" | "index">(
  chainId: number,
  marketTokenAddress: string,
  tokenType: T
): T extends "index" ? NativeTokenSupportedAddress | ERC20Address : ERC20Address {
  const market = MARKETS[chainId as ContractsChainId][marketTokenAddress];

  if (tokenType === "index") {
    return convertTokenAddress(chainId, market.indexTokenAddress, "native")! as T extends "index"
      ? NativeTokenSupportedAddress | ERC20Address
      : ERC20Address;
  }

  if (tokenType === "long") {
    return market.longTokenAddress as ERC20Address;
  }

  return market.shortTokenAddress as ERC20Address;
}

export function getMarketIndexToken(chainId: number, marketTokenAddress: string): Token {
  const indexTokenAddress = getTokenAddressByMarket(chainId, marketTokenAddress, "index");

  return getToken(chainId, indexTokenAddress);
}

export function getTokenSymbolByMarket(
  chainId: number,
  marketTokenAddress: string,
  tokenType: "long" | "short" | "index"
): string {
  const tokenAddress = getTokenAddressByMarket(chainId, marketTokenAddress, tokenType);
  return getToken(chainId, tokenAddress).symbol;
}

export function getIsSpotOnlyMarket(chainId: number, marketTokenAddress: string): boolean {
  return MARKETS[chainId as ContractsChainId][marketTokenAddress].indexTokenAddress === zeroAddress;
}

export function getMarketIsSameCollaterals(chainId: number, marketTokenAddress: string): boolean {
  const market = MARKETS[chainId][marketTokenAddress];
  if (!market) {
    return false;
  }

  return market.longTokenAddress === market.shortTokenAddress;
}
